/**
 * Raw brand colours — theme-invariant. Use only where a colour is genuinely
 * fixed. Everything else reads the semantic {@link ThemeColors} set via
 * `useTheme()` so it follows light / dark.
 */
export const palette = {
  brandDark: "#131512",
  brandSky: "#f1f5f8",
  brandSand: "#fafedd",
  brandForest: "#002c15",
  brandMint: "#3ecf8e",
} as const;

export type ColorScheme = "light" | "dark";

/** Semantic colour contract — key names shared across the app fleet. */
export type ThemeColors = {
  background: string;
  foreground: string;
  heading: string;
  surface: string;
  surfaceSunken: string;
  mutedSurface: string;
  mutedForeground: string;
  hairline: string;
  primary: string;
  primaryHover: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  border: string;
  focusRing: string;
  success: string;
  successSurface: string;
  warning: string;
  warningSurface: string;
  danger: string;
  dangerSurface: string;
  overlay: string;
};

export const lightColors: ThemeColors = {
  background: "#ffffff",
  foreground: "#131512",
  heading: "#002c15",
  surface: "#f1f5f8",
  // Warmer, slightly lighter sunken surface for page backgrounds behind cards.
  surfaceSunken: "#f5f6f2",
  mutedSurface: "#fafedd",
  mutedForeground: "#48606f",
  // Low-emphasis divider between rows / inside cards. Visible borders
  // (colors.border) are reserved for inputs, outline buttons, and card edges.
  hairline: "#e7e5dc",
  primary: "#3ecf8e",
  primaryHover: "#2fb87c",
  primaryForeground: "#002c15",
  secondary: "#f1f5f8",
  secondaryForeground: "#131512",
  accent: "#002c15",
  border: "#d5d3c8",
  focusRing: "rgba(62, 207, 142, 0.55)",
  success: "#177245",
  successSurface: "#e7f4ec",
  warning: "#a15c07",
  warningSurface: "#fff5e8",
  danger: "#b42318",
  dangerSurface: "#fff0ee",
  overlay: "rgba(0, 44, 21, 0.45)",
};

/**
 * Dark palette — derived from the light "mint / forest / sky / sand" set
 * (3 Sep 2026, pending Brad's review in the running app): near-black grounds,
 * sky-toned text, mint green held as primary.
 */
export const darkColors: ThemeColors = {
  background: "#131512",
  foreground: "#EDEDE7",
  heading: "#D8E8DF",
  surface: "#1C1F1B",
  surfaceSunken: "#161814",
  mutedSurface: "#22261E",
  mutedForeground: "#9AA8A0",
  hairline: "#2A2E27",
  primary: "#3ecf8e",
  primaryHover: "#4FD99B",
  primaryForeground: "#00160B",
  secondary: "#22261E",
  secondaryForeground: "#EDEDE7",
  accent: "#3ecf8e",
  border: "#33372F",
  focusRing: "rgba(62, 207, 142, 0.55)",
  success: "#5FBF85",
  successSurface: "#16281E",
  warning: "#D89A4A",
  warningSurface: "#2E2413",
  danger: "#F0736A",
  dangerSurface: "#2E1B19",
  overlay: "rgba(0, 0, 0, 0.55)",
};

/**
 * Back-compat alias: the light set plus the raw brand colours. New code reads
 * colours from `useTheme().colors` so a theme switch propagates; only
 * genuinely fixed colours (or non-render module constants) touch this directly.
 */
export const colors = { ...lightColors, ...palette };
